import { CompanyEntry, TaxRate as InvoiceTaxRate } from '@dreryk/egabinet-invoice-frontend-api';
import {
   MedicalServicePerformableByPractitionerSimple,
   TaxRate,
} from '@dreryk/egabinet-private-medical-service-frontend-api';
import { PractitionerId } from '@dreryk/egabinet-personnel-published-language-api';

import { assertUnreachable } from '@dreryk/react-common';

import { InvoiceItemsTableBean, MedicalServiceTaxRateMap } from './InvoiceItemsTable.types';

export const NEW_ROW_EMPTY_VALUE_PLACEHOLDER = '-';

export const getMedicalServiceName = (
   name: string | MedicalServicePerformableByPractitionerSimple | null
): string | null => {
   if (typeof name === 'string') {
      return name;
   }

   if (!name) {
      return null;
   }

   return name.name;
};

export const getCompanyName = (company: CompanyEntry | string): string => {
   if (typeof company === 'string') {
      return company;
   }

   return company.name;
};

export const mapMedicalServiceTaxRateCodeToInvoiceTaxRate = (taxRateCode: string): InvoiceTaxRate => {
   switch (taxRateCode) {
      case MedicalServiceTaxRateMap.ZW:
         return 'NO_VAT';
      case MedicalServiceTaxRateMap.ZERO:
         return 'VAT_0';
      case MedicalServiceTaxRateMap.REDUCED:
         return 'VAT_8';
      case MedicalServiceTaxRateMap.STANDARD:
         return 'VAT_23';
      default:
         return assertUnreachable(undefined);
   }
};

export const mapInvoiceItemsTaxRateToApiTaxRate = (taxRate: InvoiceTaxRate | TaxRate | null): InvoiceTaxRate | null => {
   if (typeof taxRate === 'string') {
      return taxRate;
   }

   if (!taxRate) {
      return null;
   }

   return mapMedicalServiceTaxRateCodeToInvoiceTaxRate(taxRate.code);
};

export const getTaxRateCellDefaultValue = (taxRate: InvoiceTaxRate | TaxRate | null): InvoiceTaxRate | null => {
   if (typeof taxRate === 'string') {
      return taxRate;
   }

   if (!taxRate) {
      return null;
   }

   return mapMedicalServiceTaxRateCodeToInvoiceTaxRate(taxRate.code);
};

export const addEmptyRow = (): InvoiceItemsTableBean => ({
   invoiceParameters: {
      nameEditable: true,
      nameForInvoicing: null,
      nameForReceipt: null,
      rowAddedManually: true,
      unitDefaultPrice: null,
   },
   name: null,
   quantity: null,
   taxRate: null,
   unit: null,
   unitPrice: null,
});

type MedicalServiceRowVisitorOptions = {
   nameEditable: boolean | undefined;
   rowAddedManually: boolean | undefined;
   practitionerId?: PractitionerId;
};

type MedicalServiceRowVisitor = <ACCEPT>(visitor: {
   visitManuallyAddedRow: (practitionerId?: PractitionerId) => ACCEPT;
   visitManuallyAddedRowWithName: () => ACCEPT;
   visitFetchedRow: () => ACCEPT;
}) => ACCEPT;

export const medicalServiceRowVisitor = (options: MedicalServiceRowVisitorOptions): MedicalServiceRowVisitor => (
   visitor
) => {
   const { nameEditable, rowAddedManually } = options;

   if (nameEditable && rowAddedManually) {
      return visitor.visitManuallyAddedRow(options.practitionerId);
   }

   if (!nameEditable && rowAddedManually) {
      return visitor.visitManuallyAddedRowWithName();
   }

   return visitor.visitFetchedRow();
};

const getDefaultTaxRate = (taxRates: TaxRate[]): TaxRate => {
   const foundTaxRate = taxRates.find((taxRate) => taxRate.defaultRate);
   return foundTaxRate ?? assertUnreachable(undefined);
};

export const getTaxRate = (taxRates: TaxRate[], id: number): TaxRate => {
   const foundTaxRate = taxRates.find((taxRate) => taxRate.id === id) ?? null;
   return foundTaxRate ?? getDefaultTaxRate(taxRates);
};
